// Client-side "contact aliases" (nicknames) — a device-local map of userId →
// the observer's PRIVATE label for a friend. Like favorites, an alias NEVER
// leaves the device (no server field, no API call, no log line, no push
// payload — docs/08 §2), so this is deliberately synchronous localStorage, the
// same pattern as `storage/favorites.ts`: offline-first, and an edit takes
// effect without an async round-trip.
//
// A cached snapshot backs `getAliases()` so it is referentially stable between
// writes — a hard requirement for `useSyncExternalStore` consumers.

const KEY = 'merkury.aliases';
/** `lastSyncedAt` marker for the E2E alias cloud sync (docs/08 §8.3), stored
 *  next to the alias map and wiped together with it on sign-out. */
const SYNC_MARKER_KEY = 'merkury.aliases.sync';

type Listener = () => void;
const listeners = new Set<Listener>();

let cache: Readonly<Record<string, string>> | undefined;

const EMPTY: Readonly<Record<string, string>> = Object.freeze({});

function ls(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    // Storage can throw (disabled cookies, sandboxed iframe) — treat as absent.
    return undefined;
  }
}

/**
 * Canonicalize a raw alias (docs/08 §3): trim, strip control characters, cap at
 * 64 runes (same cap as DisplayName). Pure — no browser deps — so the brain can
 * import it. An empty result means "clear the alias".
 */
export function sanitizeAlias(raw: string): string {
  // Strip C0/C1 control characters (incl. newlines/tabs) so an alias is a
  // single clean line, then trim surrounding whitespace.
  const stripped = raw.replace(/[\u0000-\u001F\u007F-\u009F]/g, '').trim();
  // Cap by RUNE (code point), not UTF-16 unit — matches the DisplayName rule.
  const runes = [...stripped];
  return runes.length > 64 ? runes.slice(0, 64).join('') : stripped;
}

function load(): Record<string, string> {
  const raw = ls()?.getItem(KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string' && v.length > 0) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function persist(map: Record<string, string>): void {
  const frozen = Object.freeze({ ...map });
  cache = frozen;
  ls()?.setItem(KEY, JSON.stringify(frozen));
  for (const listener of listeners) listener();
}

/** Subscribe to alias-map changes (for `useSyncExternalStore`). */
export function onAliasesChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current alias map. Referentially stable until it changes. */
export function getAliases(): Readonly<Record<string, string>> {
  if (cache === undefined) cache = Object.freeze(load());
  return cache;
}

/** The alias set for `userId`, or undefined if none. */
export function getAlias(userId: string): string | undefined {
  return getAliases()[userId];
}

/**
 * Set (or clear) `userId`'s alias. `alias` is trimmed + sanitized (§3); a null,
 * empty, or all-whitespace value CLEARS it. No-op if the effective value is
 * unchanged. Returns the stored alias, or null if cleared.
 */
export function setAlias(userId: string, alias: string | null): string | null {
  const current = getAliases();
  const clean = alias == null ? '' : sanitizeAlias(alias);
  if (clean === '') {
    if (!(userId in current)) return null; // already absent — no notify
    const next = { ...current };
    delete next[userId];
    persist(next);
    return null;
  }
  if (current[userId] === clean) return clean; // unchanged — no notify
  persist({ ...current, [userId]: clean });
  return clean;
}

/**
 * Replace the WHOLE alias map (docs/08 §8.3: the alias cloud sync pulls a newer
 * blob and swaps the local map wholesale). Sanitizes every entry through the §3
 * rule and drops invalid/empty ones, so a decrypted blob can never inject a
 * malformed alias. Notifies subscribers exactly like a local edit.
 */
export function replaceAliases(next: Record<string, string>): void {
  const clean: Record<string, string> = {};
  for (const [userId, value] of Object.entries(next)) {
    if (typeof value !== 'string') continue;
    const alias = sanitizeAlias(value);
    if (alias.length > 0) clean[userId] = alias;
  }
  persist(clean);
}

/** The stored `lastSyncedAt` marker, or null before the first sync. */
export function getAliasSyncMarker(): string | null {
  return ls()?.getItem(SYNC_MARKER_KEY) ?? null;
}

/** Record the server `updatedAt` this device last reconciled with (docs/08 §8.3). */
export function setAliasSyncMarker(updatedAt: string): void {
  ls()?.setItem(SYNC_MARKER_KEY, updatedAt);
}

/** Wipe all aliases — sign-out must not leave the previous account's labels.
 *  The sync marker goes with them so the next account re-pulls from scratch. */
export function clearAliases(): void {
  cache = EMPTY;
  ls()?.removeItem(KEY);
  ls()?.removeItem(SYNC_MARKER_KEY);
  for (const listener of listeners) listener();
}

/** Test seam: drop the in-memory snapshot so the next read re-hydrates. */
export function resetAliasesCache(): void {
  cache = undefined;
}
